import { addDaysIso } from "@/lib/dates";
import type { BucketKind } from "@/types";
import { byPosition, compareKeys, compareKeysNullsLast } from "@/lib/views/order";

// The TaskSerializer fields the page scopes read.
export interface ScopedTask {
  status: "pending" | "completed";
  position: string;
  listId: string;
  parentId: string | null;
  reusable: boolean;
  dueOn: string | null;
  myDayOn: string | null;
  myDayDismissedOn: string | null;
  myDayPosition?: string | null;
  myDaySequential?: boolean;
  bucket?: BucketKind | null;
  visibleFrom?: string | null;
  completedAt?: string | null;
}

// "myday", "planned", "templates" or a list's key.
export type ScopeKey = string;

export const LIST_PREFIX = "list:";

export const listScopeKey = (listId: string): ScopeKey => `${LIST_PREFIX}${listId}`;

// Mirrors the server-side scopes. Let them diverge and a task will either get stuck on
// a page it doesn't belong to, or vanish from its own until the next reload.
export function belongsToScope(task: ScopedTask, key: ScopeKey, today: string): boolean {
  if (key.startsWith(LIST_PREFIX)) {
    // Mirrors List#rows: both buckets leave pending dated tasks to Planned.
    const leftToPlanned = task.status === "pending" && task.dueOn !== null;
    return task.listId === key.slice(LIST_PREFIX.length) && task.parentId === null && !task.reusable && !leftToPlanned;
  }

  switch (key) {
    case "myday":
      // Mirrors Task.in_my_day_for + MyDayController's .not_template.
      return (task.myDayOn === today || task.dueOn === today) && task.myDayDismissedOn !== today && !task.reusable;
    case "planned":
      // PlannedController's scope (`pending.with_due_date.not_template`) has no `.roots` —
      // a subtask can carry its own due date (DetailsPanel's DueDatePicker only gates on
      // `!reusable`, not on being a root) and still show up here.
      return (task.dueOn !== null && !task.reusable) || isTimeGatedInboxRow(task, today);
    case "templates":
      return task.reusable && task.parentId === null;
    default:
      return false;
  }
}

// Mirrors Task.planned_for's undated part: inbox rows with a visibleFrom time.
export function isTimeGatedInboxRow(task: ScopedTask, today: string): boolean {
  return task.bucket === "inbox" && !!task.visibleFrom && belongsToScope(task, listScopeKey(task.listId), today);
}

// Mirrors Task::PLANNED_DAY_ORDER: a task pinned to today keeps its key for today's board.
export function keyOnDueDay(task: ScopedTask, today: string): string | null {
  return task.myDayOn !== today || task.dueOn === today ? (task.myDayPosition ?? null) : null;
}

// Planned's time-gated rows come from a separate query ordered by position alone.
function plannedKey(task: ScopedTask, today: string): string | null {
  return task.dueOn === null ? null : keyOnDueDay(task, today);
}

export function sortFor(key: ScopeKey, today: string): (a: ScopedTask, b: ScopedTask) => number {
  if (key === "myday") {
    // Any-time block before the in-order one, then NULLS LAST, then position —
    // mirrors Task.my_day_ordered.
    return (a, b) =>
      Number(!!a.myDaySequential) - Number(!!b.myDaySequential) ||
      compareKeysNullsLast(a.myDayPosition ?? null, b.myDayPosition ?? null) ||
      byPosition(a, b);
  }

  if (key === "planned") {
    // Mirrors Task.planned_for: due_on, then that day's My Day order (NULLS LAST), then position.
    return (a, b) =>
      compareKeys(a.dueOn ?? "", b.dueOn ?? "") ||
      compareKeysNullsLast(plannedKey(a, today), plannedKey(b, today)) ||
      byPosition(a, b);
  }

  return byPosition;
}

export const PLANNED_GROUP_KEYS = ["overdue", "today", "later_today", "tomorrow", "next_5_days", "later"] as const;

export type PlannedGroupKey = (typeof PLANNED_GROUP_KEYS)[number];

// Mirrors Task#due_bucket — a task's category by due_on relative to today.
export function plannedBucket(dueOn: string, today: string): Exclude<PlannedGroupKey, "later_today"> {
  if (dueOn < today) return "overdue";
  if (dueOn === today) return "today";
  if (dueOn === addDaysIso(today, 1)) return "tomorrow";
  if (dueOn <= addDaysIso(today, 6)) return "next_5_days";
  return "later";
}

// Mirrors PlannedController#show: non-empty groups in PLANNED_GROUPS order, each in the
// order the tasks arrive in (callers sort with sortFor("planned")).
export function groupPlanned<T extends ScopedTask>(sorted: T[], today: string): { key: PlannedGroupKey; tasks: T[] }[] {
  const buckets = new Map<PlannedGroupKey, T[]>();
  for (const task of sorted) {
    const key = task.dueOn === null ? "later_today" : plannedBucket(task.dueOn, today);
    const bucket = buckets.get(key);
    if (bucket) bucket.push(task);
    else buckets.set(key, [task]);
  }
  return PLANNED_GROUP_KEYS.flatMap((key) => {
    const tasks = buckets.get(key);
    return tasks ? [{ key, tasks }] : [];
  });
}
